import { Navigate, Route, useLocation, useParams } from "react-router-dom";
import { LEGACY_REDIRECTS, type LegacyRedirect } from "@/lib/legacyRedirects";

/**
 * Renders one <Route> per entry in LEGACY_REDIRECTS. Each matched
 * legacy URL renders a <Navigate replace /> to the new path, with
 * dynamic params, search string, and hash preserved.
 *
 * Use as: {legacyRedirectRoutes()} inside <Routes>.
 *
 * `replace` is used so the legacy URL never enters browser history —
 * the back button skips over it. Crawlers treat the JS-driven
 * navigation as a soft 301 and the destination's canonical tag
 * consolidates ranking signals.
 */
function RedirectTo({ to }: { to: string }) {
  const params = useParams();
  const { search, hash } = useLocation();

  // Substitute :param tokens in the destination with matched values.
  const resolved = to.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const value = params[key];
    return value ? encodeURIComponent(value) : `:${key}`;
  });

  return <Navigate to={`${resolved}${search}${hash}`} replace />;
}

export function legacyRedirectRoutes() {
  return LEGACY_REDIRECTS.map((entry: LegacyRedirect) => (
    <Route
      key={entry.from}
      path={entry.from}
      element={<RedirectTo to={entry.to} />}
    />
  ));
}
